import { useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { EVENTS } from '@/analytics/consts';
import { useAnalytics } from '@/analytics/useAnalytics';
import {
  getListAgentThreadsQueryKey,
  ThreadDto,
  ThreadResponse,
  CalendarMeetingInfoDtoType
} from '@/api/generated';
import { DEFAULT_THREAD_ID } from '@/components/AskMeAnything/types';
import { AccessType } from '@/enums/AccessType';
import RoutesMap, { generatePath } from '@/enums/RoutesMap';
import useThreads from '@/hooks/askMeAnything/useThreads';
import {
  useChatActions,
  useHasChatStarted,
  useActiveThreadId,
  useIsThreadNew,
  useChatTitle
} from '@/store/chat/chatStore';
import { useSelectedMeetings } from '@/store/selectedMeetingsStore';
import { createMeetingLink } from '@/utils/createMeetingLink';
import { launchErrorToast } from '@/utils/toast';

const useChatLogic = () => {
  const chatMeetings = useSelectedMeetings();
  const activeThreadId = useActiveThreadId();
  const chatTitle = useChatTitle();
  const isThreadNew = useIsThreadNew();
  const queryClient = useQueryClient();
  const { createThread, threads, patchThread } = useThreads();
  const { captureEvent } = useAnalytics();
  const isSingleMeeting = chatMeetings.length === 1;
  const noMeetings = chatMeetings.length === 0;
  const navigate = useNavigate();
  const {
    setChatPrompt,
    setActiveThreadId,
    setHasChatStarted,
    setIsThreadNew,
    setIsThreadsOpen,
    setIsQuickActionsOpen,
    clearChatPrompt,
    setChatTitle,
    setIsChatOpen
  } = useChatActions();
  const hasChatStarted = useHasChatStarted();
  const initialChatTitle = isSingleMeeting
    ? chatMeetings[0].title
    : noMeetings
      ? 'No Meetings'
      : 'Multiple Meetings';

  useEffect(() => {
    if (isThreadNew) {
      setChatTitle(initialChatTitle || '');
    }
  }, [isThreadNew]);

  const thumbnail = isSingleMeeting ? chatMeetings[0].thumbnail : '';

  const activeThread = useMemo(
    () => threads.find(thread => thread.id === activeThreadId) || null,
    [threads, activeThreadId]
  );

  const updateThreadSeenAt = useCallback(
    (threadId: string) => {
      patchThread({
        id: threadId,
        data: {
          seenAt: Date.now().toString()
        }
      });
    },
    [patchThread]
  );

  useEffect(() => {
    if (activeThreadId && !activeThread?.isUserCreated && !activeThread?.seenAt) {
      updateThreadSeenAt(activeThreadId);
    }
  }, [activeThreadId, updateThreadSeenAt, activeThread]);

  const handleThreadChange = useCallback(
    (thread: ThreadDto, willCloseThreads = false) => {
      if (!thread.id) return;
      if (thread.id === activeThreadId) {
        setIsThreadsOpen(false);
        setIsQuickActionsOpen(false);
        clearChatPrompt();
        return;
      }
      setIsThreadNew(false);
      setChatTitle(thread.title || '');
      setActiveThreadId(thread.id);
      if (willCloseThreads) {
        clearChatPrompt();
        setIsThreadsOpen(false);
        setIsQuickActionsOpen(false);
      }
    },
    [activeThreadId, setChatTitle]
  );

  const createThreadWithContext = useCallback(
    async (title: string | null = null, isChatPage = false) => {
      const filesContext = chatMeetings.map(meeting => {
        const isSharedMeeting = meeting?.type === CalendarMeetingInfoDtoType.privateShared;
        const accessType = isSharedMeeting ? AccessType.RESTRICTED : AccessType.ONLY_ME;
        const fileContext = {
          fileKey: meeting.transcript?.fileKey,
          bucketName: meeting.transcript?.bucketName,
          entityUri: createMeetingLink(
            isSharedMeeting ? meeting?.sharedMeetingId || '' : meeting?.id || '',
            accessType
          ),
          contextType: 'transcript' as const
        };
        return fileContext;
      });

      createThread(
        {
          data: {
            title: title || chatTitle,
            filesContext,
            textsContext: []
          }
        },
        {
          onSuccess: newThread => {
            const newThreadItem = newThread?.items?.[0];
            if (newThreadItem) {
              setActiveThreadId(newThreadItem.id || DEFAULT_THREAD_ID);
              captureEvent(EVENTS.CHAT_THREAD_GENERATED, {
                threadId: newThreadItem.id
              });
              queryClient.setQueryData(getListAgentThreadsQueryKey(), (oldData: ThreadResponse) => {
                if (!oldData) return { items: [newThreadItem] };
                return {
                  ...oldData,
                  items: [newThreadItem, ...(oldData.items || [])]
                };
              });
              if (title) {
                setChatTitle(title);
                clearChatPrompt();
                setIsThreadsOpen(false);
                setIsQuickActionsOpen(false);
              }
              if (isChatPage) {
                navigate(generatePath(RoutesMap.Chat, { threadId: newThreadItem.id }));
              }
            }
          },
          onError: () => {
            launchErrorToast('Failed to create thread');
          }
        }
      );
    },
    [createThread, chatMeetings, chatTitle, queryClient, handleThreadChange]
  );

  const handleSubmit = useCallback(
    (prompt: string, isChatPage = false) => {
      captureEvent(EVENTS.CHAT_MESSAGE_SENT);
      if (!activeThreadId) {
        createThreadWithContext(null, isChatPage);
      }
      if (!hasChatStarted) {
        setHasChatStarted(true);
      }
      setChatPrompt(prompt);
    },
    [activeThreadId, createThreadWithContext, hasChatStarted]
  );

  const unseenThreadsCount = useMemo(
    () => threads.filter(thread => thread.isUserCreated === false && !thread.seenAt).length,
    [threads]
  );

  const handleOpenQuickActions = useCallback(
    (isChatPage = false) => {
      if (isChatPage) {
        navigate(RoutesMap.QuickActions);
      } else {
        setIsQuickActionsOpen(true);
        setIsChatOpen(true);
      }
    },
    [handleSubmit, setIsQuickActionsOpen, setIsChatOpen]
  );

  return {
    unseenThreadsCount,
    chatTitle,
    setChatTitle,
    thumbnail,
    hasChatStarted,
    activeThreadId,
    activeThread,
    isThreadNew,
    handleThreadChange,
    createThreadWithContext,
    handleSubmit,
    threads,
    handleOpenQuickActions
  };
};

export default useChatLogic;
